/**
 * @project E-School Web
 * @author Vothana CHY
 *
 * Smart Banner visibility control.
 * Hides the Smart Banner on the home page ('/') and download page ('/download').
 * Works reliably even though smartbanner.js is `defer`red (may inject the element
 * long after page mount) by using a MutationObserver instead of polling.
 *
 * Strategy:
 *  1. Toggle the `hide-smartbanner` class on <html> based on current route.
 *  2. CSS (main.css) hides `.smartbanner` and resets margin-top when that class is present.
 *  3. A MutationObserver re-applies the class whenever smartbanner.js adds/removes the
 *     element from the DOM (handles deferred load + close-button removal).
 */

export default defineNuxtPlugin((nuxtApp) => {
  // Routes where the Smart Banner should NOT appear
  const EXCLUDED_ROUTES = ['/', '/download'];

  const shouldHide = (path: string): boolean => EXCLUDED_ROUTES.some((r) => path === r || path === `${r}/`);

  /** Toggle the CSS class on <html>; CSS does the actual hiding. */
  const applyClass = (path: string) => {
    document.documentElement.classList.toggle('hide-smartbanner', shouldHide(path));
  };

  // ── MutationObserver: re-apply class whenever smartbanner.js mutates the DOM ─
  // This handles:
  //   • deferred load — banner element added seconds after mount
  //   • close button — banner element removed then re-added on next visit
  const observer = new MutationObserver(() => {
    const route = useRoute();
    applyClass(route.path);
  });

  observer.observe(document.body, { childList: true, subtree: false });

  // ── Initial application ────────────────────────────────────────────────────
  nuxtApp.hook('app:mounted', () => {
    const route = useRoute();
    applyClass(route.path);
  });

  // ── Re-apply on every SPA navigation ─────────────────────────────────────
  nuxtApp.hook('page:finish', () => {
    const route = useRoute();
    applyClass(route.path);
  });

  // Clean up observer on app unmount (SSR hydration safety)
  nuxtApp.hook('app:beforeMount', () => {
    // nothing needed; observer is client-only
  });
});
